﻿import React, { useEffect, useRef, useState, Fragment } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useUserContext } from '../../../../../../shared/context/user-context';
import FailureSnackbar from '../../../../../../shared/feedback/FailureSnackbar';
import SuccessSnackbar from '../../../../../../shared/feedback/SuccessSnackbar';
import * as Features from '../../../../../../shared/Features';
import { useTelemetryContext } from '../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../telemetry/utils';
import { LinkInfo } from '../../OrderFilmApi';
import SendEmailInput from '../../SendEmailInput';
import { useEnteredLinks } from '../hooks/useEnteredLinks';
import { useDisableSendEmailOnEmptyLinks } from '../hooks/useDisableSendEmailOnEmptyLinks';
import { getSentLinks } from '../queries/getSentLinks';
import { sendLinks } from '../queries/sendLinks';
import { descendingOnDate, generateDummyArray } from '../utils';
import LinkInput from './LinkInput';
import SentLinkInformation from './SentLinkInformation';

type VideoLinksLabels = {
    linkPlaceholder: string;
    addLinkButton: string;
    sendLinksAsEmailButton: string;
    sendLinksAsEmailPlaceholder: string;
    linksSentTo: string;
    sendLinksAsEmailSuccess: string;
    sendLinksAsEmailFailure: string;
    sendEmailDynamicError: (translationKey: string) => string;
};

type ExternalProps = {
    labels: VideoLinksLabels;
    orderId: number;
};

type VideoLinksProps = {
    previouslySentLinks: LinkInfo[];
} & ExternalProps;

export type EnteredLinks = {
    [key: string]: string;
};

function VideoLinks({ orderId, labels, previouslySentLinks }: VideoLinksProps) {
    let [numberOfLinkInputs, setNumberOfLinkInputs] = useState<number>(1);
    let [showSendEmailSuccessSnackbar, setShowSendEmailSuccessSnackbar] = useState<boolean>(false);
    let [showSendEmailFailureSnackbar, setShowSendEmailFailureSnackbar] = useState<boolean>(false);
    let [dynamicError, setDynamicError] = useState<string | null>(null);
    let { user } = useUserContext();

    function handleClickAddLink() {
        setNumberOfLinkInputs((previousValue) => previousValue + 1);
    }

    function handleCloseSendEmailSuccessSnackbar() {
        setShowSendEmailSuccessSnackbar(false);
    }

    function handleCloseSendEmailFailureSnackbar() {
        setShowSendEmailFailureSnackbar(false);
    }

    function clearLinkForm() {
        if (linkInputsRef.current) {
            linkInputsRef.current.reset();
        }
    }

    let { enteredLinks, setEnteredLinks } = useEnteredLinks(numberOfLinkInputs);

    let disableEmailButton = useDisableSendEmailOnEmptyLinks(enteredLinks);

    let queryClient = useQueryClient();

    let sendEmailMutation = useMutation(
        ({ email, links }: { email: string; links: string[] }) => sendLinks(orderId, email, links),
        {
            onSuccess: () => {
                setShowSendEmailSuccessSnackbar(true);
                setNumberOfLinkInputs(1);
                clearLinkForm();
                return queryClient.invalidateQueries('getLinkInfo');
            },
            onError: async (error) => {
                // @ts-ignore
                if (error.response) {
                    // @ts-ignore
                    let { errors } = await error.response.json();
                    if (errors && errors.length > 0) {
                        let [first, _] = errors;
                        let { value } = first;
                        setDynamicError(value);
                    } else {
                        setDynamicError(labels.sendLinksAsEmailFailure);
                    }
                } else {
                    setDynamicError(labels.sendLinksAsEmailFailure);
                }
            }
        }
    );

    useEffect(() => {
        if (!showSendEmailFailureSnackbar) {
            setShowSendEmailFailureSnackbar(true);
        }
    }, [dynamicError]);

    let linkInputsRef = useRef<HTMLFormElement | null>(null);

    return (
        <>
            {Features.hasFeature(Features.UploadFilm, user) ? (
                <>
                    <form
                        ref={linkInputsRef}
                        onSubmit={(event) => {
                            event.preventDefault();
                        }}
                    >
                        {generateDummyArray(numberOfLinkInputs).map((_, index) => {
                            return (
                                <LinkInput
                                    key={index}
                                    name={`link-${index}`}
                                    placeholder={labels.linkPlaceholder}
                                    onChange={(link: string) => {
                                        setEnteredLinks((prev) => {
                                            return {
                                                ...prev,
                                                [`link-${index}`]: link
                                            };
                                        });
                                    }}
                                />
                            );
                        })}
                    </form>
                    <Box sx={{ padding: '8px' }}>
                        <Button
                            variant="text"
                            size="small"
                            color="primary"
                            onClick={handleClickAddLink}
                            sx={{ padding: '4px 6px', fontSize: '14px' }}
                        >
                            {labels.addLinkButton}
                        </Button>
                    </Box>
                </>
            ) : null}
            {Features.hasFeature(Features.SendFilm, user) ? (
                <>
                    <Divider />
                    <SendEmailInput
                        onSend={(email: string) => {
                            let links = Object.values(enteredLinks).filter(
                                (link) => link.length > 0
                            );
                            sendEmailMutation.mutate({ email, links });
                        }}
                        labels={{
                            sendButton: labels.sendLinksAsEmailButton,
                            placeholder: labels.sendLinksAsEmailPlaceholder
                        }}
                        buttonDisabled={disableEmailButton}
                    />
                </>
            ) : null}
            {previouslySentLinks && previouslySentLinks.length > 0 ? (
                <>
                    <Divider />
                    {previouslySentLinks
                        .sort(descendingOnDate)
                        .flatMap((sentLinks, index) =>
                            index < previouslySentLinks.length - 1 ? (
                                <Fragment key={sentLinks.linksSentTime}>
                                    <SentLinkInformation
                                        labels={{ sentTo: labels.linksSentTo }}
                                        links={sentLinks.links}
                                        sentToEmail={sentLinks.sentToEmail}
                                        linksSentTime={new Date(sentLinks.linksSentTime)}
                                    />
                                    <Divider />
                                </Fragment>
                            ) : (
                                <SentLinkInformation
                                    key={sentLinks.linksSentTime}
                                    labels={{ sentTo: labels.linksSentTo }}
                                    links={sentLinks.links}
                                    sentToEmail={sentLinks.sentToEmail}
                                    linksSentTime={new Date(sentLinks.linksSentTime)}
                                />
                            )
                        )}
                </>
            ) : null}
            <SuccessSnackbar
                open={showSendEmailSuccessSnackbar}
                onClose={handleCloseSendEmailSuccessSnackbar}
                text={labels.sendLinksAsEmailSuccess}
            />
            <FailureSnackbar
                open={showSendEmailFailureSnackbar}
                onClose={handleCloseSendEmailFailureSnackbar}
                text={labels.sendEmailDynamicError(dynamicError)}
            />
        </>
    );
}

function DataLayer(props: ExternalProps) {
    let { logger } = useTelemetryContext();
    let { user } = useUserContext();

    let { isError, isLoading, data, error } = useQuery(['getLinkInfo'], () =>
        getSentLinks(props.orderId)
    );

    if (isError && logger) {
        let additionalInfo = {
            origin: 'VideoFileUpload.DataLayer.getOrderFilm',
            userInfo: getUserInfo(user)
        };
        logger.logException(error, additionalInfo);
    }

    return <VideoLinks {...props} previouslySentLinks={isLoading ? null : data} />;
}

export default DataLayer;
